// ----------------------------------------------------------------------
// gotrueAuth: raw HTTP calls to Supabase GoTrue endpoints.
// Bypasses supabase-js auth client which has a known bug on mobile Safari
// ("Failed to execute 'json' on 'Response': body stream already read")
// in v2.106 — internally reads the response body twice in error paths.
//
// We:
//   1. fetch GoTrue endpoints directly with raw fetch
//   2. buffer the body ONCE via response.text()
//   3. JSON.parse manually
//   4. on success, hand the session to supabase-js via setSession()
//      → triggers SIGNED_IN auth state change → AuthContext picks it up
// ----------------------------------------------------------------------
import { supabase } from "./supabaseClient";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function gotrue(path, payload) {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error("Autenticação não configurada.");
  }
  const url = `${SUPABASE_URL.replace(/\/+$/, "")}/auth/v1/${path.replace(/^\/+/, "")}`;

  // Use XMLHttpRequest instead of fetch — fetch has well-known mobile Safari /
  // wrapper-incompatibility issues ("Body is disturbed or locked" / "body
  // stream already read") that occur in supabase-js v2.106 internals.
  // XHR has no body stream concept — it gives us the response text once.
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.setRequestHeader("apikey", SUPABASE_ANON_KEY);
      xhr.setRequestHeader("Authorization", `Bearer ${SUPABASE_ANON_KEY}`);
      xhr.timeout = 20000;
      xhr.onload = () => {
        let body = {};
        try { body = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
        catch (_) { body = { msg: xhr.responseText }; }
        const ok = xhr.status >= 200 && xhr.status < 300;
        resolve({ status: xhr.status, ok, body });
      };
      xhr.onerror = () => reject(new Error("Sem ligação ao servidor de autenticação."));
      xhr.ontimeout = () => reject(new Error("Tempo esgotado a contactar o servidor."));
      xhr.send(JSON.stringify(payload));
    } catch (e) {
      reject(new Error("Sem ligação ao servidor de autenticação."));
    }
  });
}

function isUserExistsError(body) {
  const msg = (body?.msg || body?.message || body?.error_description || body?.error || "").toLowerCase();
  return (
    msg.includes("already registered") ||
    msg.includes("already been registered") ||
    msg.includes("user already") ||
    msg.includes("already exists") ||
    body?.code === "user_already_exists" ||
    body?.error_code === "user_already_exists"
  );
}

function isEmailNotConfirmedError(body) {
  const msg = (body?.msg || body?.message || body?.error_description || body?.error || "").toLowerCase();
  return (
    msg.includes("email not confirmed") ||
    msg.includes("not confirmed") ||
    body?.error_code === "email_not_confirmed"
  );
}

function isInvalidCredentialsError(body) {
  const msg = (body?.msg || body?.message || body?.error_description || body?.error || "").toLowerCase();
  return (
    msg.includes("invalid login") ||
    msg.includes("invalid credentials") ||
    body?.error_code === "invalid_credentials"
  );
}

// ----------------------------------------------------------------------
// signUp: POST /auth/v1/signup
// Returns: { session, user, needsConfirmation, exists, error }
// ----------------------------------------------------------------------
export async function rawSignUp(email, password) {
  const { status, ok, body } = await gotrue("signup", { email, password });
  if (ok) {
    // Supabase returns either a full session (auto-confirm enabled) or just a user
    // (email confirmation required — session is null).
    const accessToken = body?.access_token || body?.session?.access_token;
    const refreshToken = body?.refresh_token || body?.session?.refresh_token;
    if (accessToken && refreshToken) {
      await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
      return { ok: true, session: { access_token: accessToken, refresh_token: refreshToken }, user: body?.user || null };
    }
    // No session → email confirmation pending
    return { ok: true, session: null, needsConfirmation: true, user: body?.user || null };
  }
  // Error path
  if (isUserExistsError(body)) {
    return { ok: false, exists: true };
  }
  return { ok: false, error: body?.msg || body?.message || body?.error_description || `Erro ${status}` };
}

// ----------------------------------------------------------------------
// updatePassword: PUT /auth/v1/user (Bearer token required)
// Requires an active session (e.g. just established via recovery flow).
// ----------------------------------------------------------------------
export async function rawUpdatePassword(newPassword) {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error("Autenticação não configurada.");
  }
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) {
    return { ok: false, error: "Sessão expirada. Pede um novo link de recuperação." };
  }
  const url = `${SUPABASE_URL.replace(/\/+$/, "")}/auth/v1/user`;
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.setRequestHeader("apikey", SUPABASE_ANON_KEY);
      xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
      xhr.timeout = 20000;
      xhr.onload = () => {
        let body = {};
        try { body = xhr.responseText ? JSON.parse(xhr.responseText) : {}; } catch (_) { body = {}; }
        const ok = xhr.status >= 200 && xhr.status < 300;
        if (ok) {
          resolve({ ok: true });
        } else {
          resolve({
            ok: false,
            error: body?.msg || body?.message || body?.error_description || `Erro ${xhr.status}`,
          });
        }
      };
      xhr.onerror = () => reject(new Error("Sem ligação ao servidor."));
      xhr.ontimeout = () => reject(new Error("Tempo esgotado."));
      xhr.send(JSON.stringify({ password: newPassword }));
    } catch (e) {
      reject(new Error("Sem ligação ao servidor."));
    }
  });
}
export async function rawSignIn(email, password) {
  const { status, ok, body } = await gotrue("token?grant_type=password", { email, password });
  if (ok && body?.access_token && body?.refresh_token) {
    await supabase.auth.setSession({
      access_token: body.access_token,
      refresh_token: body.refresh_token,
    });
    return { ok: true, session: { access_token: body.access_token, refresh_token: body.refresh_token }, user: body?.user || null };
  }
  if (isEmailNotConfirmedError(body)) {
    return { ok: false, needsConfirmation: true };
  }
  if (isInvalidCredentialsError(body)) {
    return { ok: false, invalidCredentials: true };
  }
  return { ok: false, error: body?.msg || body?.message || body?.error_description || `Erro ${status}` };
}

// ----------------------------------------------------------------------
// signOut: POST /auth/v1/logout (Bearer token) + clear local storage.
// Robust against the supabase-js body-stream bug — uses XHR and always
// purges localStorage even if the HTTP call fails.
// ----------------------------------------------------------------------
export async function rawSignOut() {
  let accessToken = null;
  try {
    const { data } = await supabase.auth.getSession();
    accessToken = data?.session?.access_token || null;
  } catch (_) { /* ignore */ }

  const url = `${(SUPABASE_URL || "").replace(/\/+$/, "")}/auth/v1/logout`;

  // Fire the logout call. We don't await it (and don't care if it fails) —
  // what matters is that we clear localStorage and in-memory session below.
  if (accessToken && SUPABASE_URL && SUPABASE_ANON_KEY) {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      xhr.setRequestHeader("apikey", SUPABASE_ANON_KEY);
      xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
      xhr.timeout = 5000;
      xhr.send(null);
    } catch (_) { /* ignore */ }
  }

  // Tell supabase-js to drop its in-memory + storage session.
  try { await supabase.auth.signOut({ scope: "local" }); } catch (_) { /* ignore */ }

  // Belt-and-suspenders: purge every Supabase-related key from localStorage.
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      const toRemove = [];
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const k = window.localStorage.key(i);
        if (!k) continue;
        if (
          k === "cec-supabase-auth-v3" ||
          k.startsWith("sb-") ||
          k.startsWith("supabase.auth.") ||
          k.includes("supabase")
        ) {
          toRemove.push(k);
        }
      }
      toRemove.forEach((k) => { try { window.localStorage.removeItem(k); } catch (_) {} });
      // Also clear sessionStorage just in case
      try { window.sessionStorage?.removeItem?.("cec-supabase-auth-v3"); } catch (_) {}
    }
  } catch (_) { /* ignore */ }

  return { ok: true };
}

